import React, { useEffect, useState, useRef } from 'react';
import Papa from 'papaparse';
import { Routes, Route, Link } from 'react-router-dom';
import { collection, getDocs, limit, query, deleteDoc, doc } from 'firebase/firestore';

import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/Button';
import { AddGuestModal } from '@/components/admin/AddGuestModal';
import { ImportGuestsModal } from '@/components/admin/ImportGuestsModal';
import { EditGuestModal } from '@/components/admin/EditGuestModal';
import { GuestStats } from '@/components/admin/GuestStats';
import { EventsManager } from '@/components/admin/EventsManager';
import { AnnouncementsManager } from '@/components/admin/AnnouncementsManager';
import { TablePlanViewer } from '@/components/admin/TablePlanViewer';

const AdminHome: React.FC = () => {
    const [counts, setCounts] = useState({ guests: 0, events: 0, announcements: 0, tables: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCounts = async () => {
            const [guestSn, eventSn, announceSn, tablesSn] = await Promise.all([
                getDocs(query(collection(db, 'guests'), limit(100))),
                getDocs(query(collection(db, 'events'), limit(100))),
                getDocs(query(collection(db, 'announcements'), limit(100))),
                getDocs(query(collection(db, 'tables'), limit(100)))
            ]);
            setCounts({
                guests: guestSn.size,
                events: eventSn.size,
                announcements: announceSn.size,
                tables: tablesSn.size
            });
            setLoading(false);
        };
        fetchCounts();
    }, []);

    return (
        <div className="space-y-10 animate-fade-in">
            <header>
                <p className="eyebrow mb-2">Backstage</p>
                <h2 className="text-3xl md:text-4xl font-serif text-secondary tracking-tight">Dashboard</h2>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                <Link to="guests" className="p-8 bg-white rounded-[32px] shadow-card border border-gray-100 hover:shadow-card-hover transition-all border-b-4 border-b-primary/40 group hover:-translate-y-1">
                    <h3 className="text-sm font-black text-secondary-light uppercase tracking-[0.2em] mb-2 group-hover:text-primary transition-colors">Guests</h3>
                    <p className="text-4xl font-bold text-secondary">{loading ? '...' : counts.guests}</p>
                    <p className="text-[10px] text-secondary-light/60 mt-2 font-medium">Total guest records</p>
                </Link>
                <Link to="events" className="p-8 bg-white rounded-[32px] shadow-card border border-gray-100 hover:shadow-card-hover transition-all border-b-4 border-b-secondary/20 group hover:-translate-y-1">
                    <h3 className="text-sm font-black text-secondary-light uppercase tracking-[0.2em] mb-2 group-hover:text-secondary transition-colors">Events</h3>
                    <p className="text-4xl font-bold text-secondary">{loading ? '...' : counts.events}</p>
                    <p className="text-[10px] text-secondary-light/60 mt-2 font-medium">Itinerary items</p>
                </Link>
                <Link to="announcements" className="p-8 bg-white rounded-[32px] shadow-card border border-gray-100 hover:shadow-card-hover transition-all border-b-4 border-b-accent/20 group hover:-translate-y-1">
                    <h3 className="text-sm font-black text-secondary-light uppercase tracking-[0.2em] mb-2 group-hover:text-accent transition-colors">Announcements</h3>
                    <p className="text-4xl font-bold text-secondary">{loading ? '...' : counts.announcements}</p>
                    <p className="text-[10px] text-secondary-light/60 mt-2 font-medium">Guest updates</p>
                </Link>
                <Link to="table-plan" className="p-8 bg-white rounded-[32px] shadow-card border border-gray-100 hover:shadow-card-hover transition-all border-b-4 border-b-primary-dark/30 group hover:-translate-y-1">
                    <h3 className="text-sm font-black text-secondary-light uppercase tracking-[0.2em] mb-2 group-hover:text-primary-dark transition-colors">Table Plan</h3>
                    <p className="text-4xl font-bold text-secondary">{loading ? '...' : counts.tables}</p>
                    <p className="text-[10px] text-secondary-light/60 mt-2 font-medium">Seating arrangement</p>
                </Link>
            </div>
        </div>
    );
};

type ColumnId = 'name' | 'type' | 'code' | 'events' | 'rsvp' | 'busIe' | 'busEsTo' | 'busEsReturn' | 'plusOne' | 'children' | 'wp' | 'country' | 'table' | 'hotel' | 'dietary' | 'notes' | 'actions';

const COLUMNS: { id: ColumnId; label: string; alwaysVisible?: boolean }[] = [
    { id: 'name', label: 'Name', alwaysVisible: true },
    { id: 'type', label: 'Type' },
    { id: 'code', label: 'Code' },
    { id: 'events', label: 'Events' },
    { id: 'rsvp', label: 'RSVP (IE / ES / PB)' },
    { id: 'busIe', label: 'IE Bus' },
    { id: 'busEsTo', label: 'ES Bus (To)' },
    { id: 'busEsReturn', label: 'ES Bus (Return)' },
    { id: 'plusOne', label: 'Plus One' },
    { id: 'children', label: 'Children' },
    { id: 'wp', label: 'WP' },
    { id: 'country', label: 'Country' },
    { id: 'table', label: 'Table' },
    { id: 'hotel', label: 'Hotel' },
    { id: 'dietary', label: 'Dietary' },
    { id: 'notes', label: 'Notes' },
    { id: 'actions', label: 'Edit', alwaysVisible: true }
];

function expandGuests(guests: any[]): any[] {
    const expanded: any[] = [];
    const childrenGeneratedFor = new Set<string>();

    for (const g of guests) {
        expanded.push({
            ...g,
            _guestType: 'primary',
            _parentId: g.id,
            _displayName: g.fullName,
            _rowKey: g.id,
        });

        if (g.plusOneName) {
            expanded.push({
                ...g,
                _guestType: 'plusOne',
                _parentId: g.id,
                _displayName: g.plusOneName,
                _rowKey: `${g.id}_plusOne`,
            });
        }

        const kidsCount = typeof g.children === 'number' ? g.children : 0;
        if (g.plusChildren && kidsCount > 0 && !childrenGeneratedFor.has(g.inviteCodeHash)) {
            childrenGeneratedFor.add(g.inviteCodeHash);
            const childNames: string[] = g.childrenNames || [];
            for (let i = 0; i < kidsCount; i++) {
                expanded.push({
                    ...g,
                    _guestType: 'child',
                    _parentId: g.id,
                    _childIndex: i,
                    _displayName: childNames[i] || `Child ${i + 1}`,
                    _rowKey: `${g.id}_child_${i}`,
                });
            }
        }
    }

    return expanded;
}

const Guests: React.FC = () => {
    const [guests, setGuests] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState<string>('all');
    const [tableFilter, setTableFilter] = useState<string>('all');
    const [typeFilter, setTypeFilter] = useState<string>('all');

    const [sortColumn, setSortColumn] = useState<ColumnId | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const handleSort = (columnId: ColumnId) => {
        if (columnId === 'actions' || columnId === 'events') return;
        if (sortColumn === columnId) {
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else {
                setSortColumn(null);
                setSortDirection('asc');
            }
        } else {
            setSortColumn(columnId);
            setSortDirection('asc');
        }
    };

    const getSortValue = (g: any, columnId: ColumnId): string | number => {
        switch (columnId) {
            case 'name': return (g._displayName || '').toLowerCase();
            case 'type': return g._guestType === 'primary' ? 0 : g._guestType === 'plusOne' ? 1 : 2;
            case 'code': return (g.inviteCode || '').toLowerCase();
            case 'rsvp': {
                const ie = g.status?.ie || '';
                const es = g.status?.es || '';
                const pb = g.status?.pb || '';
                return `${ie}/${es}/${pb}`;
            }
            case 'busIe': return g.bus?.ie || '';
            case 'busEsTo': return g.bus?.esTo || '';
            case 'busEsReturn': return g.bus?.esReturn || '';
            case 'plusOne': {
                if (g._guestType !== 'primary') return '';
                return g.plusOneName || (g.plusOneAllowed ? 'zzz_allowed' : '');
            }
            case 'children': {
                if (g._guestType !== 'primary') return -1;
                return typeof g.children === 'number' ? g.children : (g.plusChildren ? 0 : -1);
            }
            case 'wp': return g._guestType === 'primary' && g.weddingParty ? 0 : 1;
            case 'country': return (g.country || 'IE').toLowerCase();
            case 'table': return g.tableNumber || '';
            case 'hotel': return (g.accommodation || '').toLowerCase();
            case 'dietary': {
                if (!g.dietary) return '';
                if (typeof g.dietary === 'string') {
                    return g._guestType === 'primary' ? g.dietary.toLowerCase() : '';
                }
                if (g._guestType === 'plusOne') return (g.dietary.plusOne || '').toLowerCase();
                if (g._guestType === 'child') return ((g.dietary.children || [])[g._childIndex] || '').toLowerCase();
                return (g.dietary.guest || '').toLowerCase();
            }
            case 'notes': return g._guestType === 'primary' ? (g.notes || '').toLowerCase() : '';
            default: return '';
        }
    };

    const [isAddGuestModalOpen, setIsAddGuestModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isEditGuestModalOpen, setIsEditGuestModalOpen] = useState(false);
    const [selectedGuest, setSelectedGuest] = useState<any | null>(null);

    const [visibleColumns, setVisibleColumns] = useState<Record<ColumnId, boolean>>(() => {
        const saved = localStorage.getItem('guestTableColumns');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {}
        }
        const defaults: Record<string, boolean> = {};
        COLUMNS.forEach(c => defaults[c.id] = true);
        return defaults;
    });

    const [isColumnDropdownOpen, setIsColumnDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        localStorage.setItem('guestTableColumns', JSON.stringify(visibleColumns));
    }, [visibleColumns]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsColumnDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchGuests = async () => {
        setLoading(true);
        const sn = await getDocs(collection(db, 'guests'));
        setGuests(sn.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
    };

    useEffect(() => {
        fetchGuests();
    }, []);

    const expandedGuests = expandGuests(guests);

    const filteredGuests = expandedGuests.filter(g => {
        const matchesSearch = g._displayName?.toLowerCase().includes(searchTerm.toLowerCase());
        
        let matchesFilter = true;
        if (filter !== 'all') {
            if (filter.startsWith('BUS_')) {
                if (filter === 'BUS_IE_YES') matchesFilter = g.bus?.ie === 'yes';
                else if (filter === 'BUS_IE_NONE') matchesFilter = g.bus?.ie === 'none';
                else if (filter === 'BUS_ES_TO_YES') matchesFilter = g.bus?.esTo === 'yes';
                else if (filter === 'BUS_ES_TO_NONE') matchesFilter = g.bus?.esTo === 'none';
                else if (filter === 'BUS_ES_RET_EARLY') matchesFilter = g.bus?.esReturn === 'early';
                else if (filter === 'BUS_ES_RET_LATE') matchesFilter = g.bus?.esReturn === 'late';
                else if (filter === 'BUS_ES_RET_NONE') matchesFilter = g.bus?.esReturn === 'none';
            } else {
                const [event, status] = filter.split('_');
                const isInvited = g.events?.includes(event);
                
                if (!isInvited) {
                    matchesFilter = false;
                } else if (status === 'INVITED') {
                    matchesFilter = true;
                } else if (status === 'YES') {
                    matchesFilter = g.status?.[event.toLowerCase()] === 'yes';
                } else if (status === 'NO') {
                    matchesFilter = g.status?.[event.toLowerCase()] === 'no';
                } else if (status === 'PENDING') {
                    const s = g.status?.[event.toLowerCase()];
                    matchesFilter = !s || s === '';
                }
            }
        }
        
        return matchesSearch && matchesFilter;
    });

    const uniqueTables = [...new Set(guests.map(g => g.tableNumber).filter(Boolean))].sort();

    const tableFilteredGuests = filteredGuests.filter(g => {
        if (tableFilter === 'all') return true;
        if (tableFilter === '__none__') return !g.tableNumber;
        return g.tableNumber === tableFilter;
    });

    const finalGuests = tableFilteredGuests.filter(g => {
        if (typeFilter === 'all') return true;
        return g._guestType === typeFilter;
    });

    const sortedGuests = sortColumn
        ? [...finalGuests].sort((a, b) => {
            const aVal = getSortValue(a, sortColumn);
            const bVal = getSortValue(b, sortColumn);
            let cmp: number;
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                cmp = aVal - bVal;
            } else {
                cmp = String(aVal).localeCompare(String(bVal));
            }
            return sortDirection === 'asc' ? cmp : -cmp;
        })
        : finalGuests;

    const handleDeleteGuest = async (guestId: string, guestName: string) => {
        if (window.confirm(`Are you sure you want to delete ${guestName}? This cannot be undone.`)) {
            try {
                await deleteDoc(doc(db, 'guests', guestId));
                fetchGuests();
            } catch (error) {
                console.error("Error deleting guest:", error);
                alert("Failed to delete guest.");
            }
        }
    };

    const handleExportCSV = () => {
        let totalPrimary = 0;
        let totalPlusOnes = 0;
        let totalChildren = 0;
        let totalRSVPs = 0;
        let totalYetToRSVP = 0;
        let ieYes = 0;
        let esYes = 0;
        let pbYes = 0;

        const data = finalGuests.map((g: any) => {
            if (g._guestType === 'primary') totalPrimary++;
            else if (g._guestType === 'plusOne') totalPlusOnes++;
            else if (g._guestType === 'child') totalChildren++;

            if (g._guestType === 'primary') {
                const hasRSVP = Boolean(g.status && (g.status.ie || g.status.es || g.status.pb));
                if (hasRSVP) totalRSVPs++;
                else totalYetToRSVP++;
            }

            if (g.status?.ie === 'yes') ieYes++;
            if (g.status?.es === 'yes') esYes++;
            if (g.status?.pb === 'yes') pbYes++;

            let dietaryGuest = '';
            let dietaryPlusOne = '';
            let dietaryChildren: string[] = [];
            if (g.dietary) {
                if (typeof g.dietary === 'string') dietaryGuest = g.dietary;
                else {
                    dietaryGuest = g.dietary.guest || '';
                    dietaryPlusOne = g.dietary.plusOne || '';
                    dietaryChildren = g.dietary.children || [];
                }
            }

            const typeLabel = g._guestType === 'primary' ? 'Primary' : g._guestType === 'plusOne' ? '+1' : 'Child';

            return {
                'Full Name': g._displayName,
                'Type': typeLabel,
                'Invite Code': g.inviteCode || '',
                'Events': (g.events || []).join(', '),
                'RSVP Ireland': g.status?.ie || '-',
                'RSVP Spain': g.status?.es || '-',
                'RSVP Pre-Boda': g.status?.pb || '-',
                'IE Bus': g.bus?.ie || '-',
                'ES Bus To': g.bus?.esTo || '-',
                'ES Bus Return': g.bus?.esReturn || '-',
                'Dietary': g._guestType === 'plusOne' ? dietaryPlusOne : (g._guestType === 'child' ? (dietaryChildren[g._childIndex] || '') : dietaryGuest),
                'Wedding Party': g._guestType === 'primary' && g.weddingParty ? 'Yes' : '',
                'Country': g.country || 'IE',
                'Table': g.tableNumber || '',
                'Hotel': g.accommodation || '',
                'Check-in': g.accommodationCheckIn || '',
                'Check-out': g.accommodationCheckOut || '',
                'Notes': g._guestType === 'primary' ? (g.notes || '') : ''
            };
        });

        const emptyRow = {
            'Full Name': '', 'Type': '', 'Invite Code': '', 'Events': '', 'RSVP Ireland': '', 'RSVP Spain': '', 'RSVP Pre-Boda': '', 
            'IE Bus': '', 'ES Bus To': '', 'ES Bus Return': '',
            'Dietary': '', 'Wedding Party': '', 'Country': '', 'Table': '', 'Hotel': '', 'Check-in': '', 'Check-out': '', 'Notes': ''
        };

        data.push({...emptyRow});
        
        data.push({
            ...emptyRow,
            'Full Name': 'TOTALS (PEOPLE ATTENDING)', 'RSVP Ireland': ieYes, 'RSVP Spain': esYes, 'RSVP Pre-Boda': pbYes
        });
        
        data.push({
            ...emptyRow,
            'Full Name': 'Total Primary Guests', 'Invite Code': totalPrimary
        });

        data.push({
            ...emptyRow,
            'Full Name': 'Total Plus Ones', 'Invite Code': totalPlusOnes
        });

        data.push({
            ...emptyRow,
            'Full Name': 'Total Children', 'Invite Code': totalChildren
        });

        data.push({
            ...emptyRow,
            'Full Name': 'Total RSVPs Received', 'Invite Code': totalRSVPs
        });

        data.push({
            ...emptyRow,
            'Full Name': 'Yet to RSVP', 'Invite Code': totalYetToRSVP
        });

        const csv = Papa.unparse(data);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `guests_export_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) return <div className="p-8">Loading guests...</div>;

    return (
        <div className="space-y-8 animate-fade-in font-sans">
            <div className="flex justify-between items-end flex-wrap gap-6">
                <div className="space-y-1">
                    <p className="eyebrow">Guest List</p>
                    <div className="flex items-center gap-3">
                        <h3 className="text-3xl font-serif text-secondary">Guests</h3>
                        <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-primary/5">{finalGuests.length} Total</span>
                    </div>
                </div>
                <div className="flex gap-3">
                    <Button onClick={handleExportCSV} variant="outline" size="sm">Export CSV</Button>
                    <Button onClick={() => setIsImportModalOpen(true)} variant="ghost" size="sm">Import CSV</Button>
                    <Button onClick={() => setIsAddGuestModalOpen(true)} size="sm">Add Guest</Button>
                    <Link to=".." className="text-secondary-light hover:text-primary text-sm font-black uppercase tracking-widest py-2 px-2 transition-colors">
                        Back
                    </Link>
                </div>
            </div>

            {/* Filters */}
            <div className="flex gap-4 flex-wrap bg-white/50 p-3 rounded-2xl border border-gray-100 backdrop-blur-sm shadow-sm relative z-30">
                <input
                    type="text"
                    placeholder="Search by name..."
                    className="p-2.5 bg-white border border-gray-200 rounded-xl w-64 outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary text-base transition-all"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
                <select
                    className="p-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary text-base transition-all"
                    value={filter}
                    onChange={(e: any) => setFilter(e.target.value)}
                >
                    <option value="all">All Guests</option>
                    <optgroup label="Ireland">
                        <option value="IE_INVITED">Invited to Ireland</option>
                        <option value="IE_YES">RSVP'd Yes to Ireland</option>
                        <option value="IE_NO">RSVP'd No to Ireland</option>
                        <option value="IE_PENDING">Awaiting RSVP for Ireland</option>
                    </optgroup>
                    <optgroup label="Spain">
                        <option value="ES_INVITED">Invited to Spain</option>
                        <option value="ES_YES">RSVP'd Yes to Spain</option>
                        <option value="ES_NO">RSVP'd No to Spain</option>
                        <option value="ES_PENDING">Awaiting RSVP for Spain</option>
                    </optgroup>
                    <optgroup label="Pre-Boda">
                        <option value="PB_INVITED">Invited to Pre-Boda</option>
                        <option value="PB_YES">RSVP'd Yes to Pre-Boda</option>
                        <option value="PB_NO">RSVP'd No to Pre-Boda</option>
                        <option value="PB_PENDING">Awaiting RSVP for Pre-Boda</option>
                    </optgroup>
                    <optgroup label="Transport">
                        <option value="BUS_IE_YES">IE Bus: Yes</option>
                        <option value="BUS_IE_NONE">IE Bus: No</option>
                        <option value="BUS_ES_TO_YES">ES Bus To: Yes</option>
                        <option value="BUS_ES_TO_NONE">ES Bus To: No</option>
                        <option value="BUS_ES_RET_EARLY">ES Bus Return: Early</option>
                        <option value="BUS_ES_RET_LATE">ES Bus Return: Late</option>
                        <option value="BUS_ES_RET_NONE">ES Bus Return: No</option>
                    </optgroup>
                </select>
                <select
                    className="p-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary text-base transition-all"
                    value={tableFilter}
                    onChange={(e: any) => setTableFilter(e.target.value)}
                >
                    <option value="all">All Tables</option>
                    <option value="__none__">No Table Assigned</option>
                    {uniqueTables.map(t => (
                        <option key={t} value={t}>{t}</option>
                    ))}
                </select>
                <select
                    className="p-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary text-base transition-all"
                    value={typeFilter}
                    onChange={(e: any) => setTypeFilter(e.target.value)}
                >
                    <option value="all">All Types</option>
                    <option value="primary">Primary</option>
                    <option value="plusOne">Plus One</option>
                    <option value="child">Child</option>
                </select>
                
                <div className="relative ml-auto" ref={dropdownRef}>
                    <button
                        onClick={() => setIsColumnDropdownOpen(!isColumnDropdownOpen)}
                        className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 text-secondary font-medium text-base transition-all flex items-center gap-2"
                    >
                        <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                        </svg>
                        Columns
                        <svg className={`w-4 h-4 transition-transform ${isColumnDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {isColumnDropdownOpen && (
                        <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-card border border-gray-100 z-50 p-2 py-3 origin-top-right animate-fade-in sm:w-56">
                            <p className="px-3 pb-2 text-xs font-bold text-secondary-light uppercase tracking-widest border-b border-gray-100 mb-2">Show Columns</p>
                            <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
                                {COLUMNS.map((col) => (
                                    <label
                                        key={col.id}
                                        className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${col.alwaysVisible ? 'opacity-60 cursor-not-allowed bg-gray-50' : 'hover:bg-bg-subtle'}`}
                                        title={col.alwaysVisible ? "This column is required" : `Toggle ${col.label} column`}
                                    >
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 rounded text-primary focus:ring-primary/20 border-gray-300 transition-colors"
                                            checked={visibleColumns[col.id]}
                                            disabled={col.alwaysVisible}
                                            onChange={(e) => {
                                                if (!col.alwaysVisible) {
                                                    setVisibleColumns(prev => ({
                                                        ...prev,
                                                        [col.id]: e.target.checked
                                                    }));
                                                }
                                            }}
                                        />
                                        <span className={`text-sm font-medium ${col.alwaysVisible ? 'text-secondary-light' : 'text-secondary'}`}>{col.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-card border border-gray-200 overflow-x-auto font-sans">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-bg-subtle">
                        <tr>
                            {COLUMNS.filter(col => visibleColumns[col.id]).map(col => {
                                const isSortable = col.id !== 'actions' && col.id !== 'events';
                                const isActive = sortColumn === col.id;
                                const isActions = col.id === 'actions';
                                
                                return (
                                    <th
                                        key={col.id}
                                        className={`px-6 py-4 text-left text-sm font-bold uppercase tracking-widest${
                                            isActions
                                                ? ' text-secondary-light bg-bg-subtle sticky right-0 z-20 border-l border-gray-100'
                                                : isActive ? ' text-secondary' : ' text-secondary-light'
                                        }${isSortable ? ' cursor-pointer select-none hover:text-secondary transition-colors' : ''}`}
                                        onClick={isSortable ? () => handleSort(col.id) : undefined}
                                    >
                                        <span className="inline-flex items-center gap-1.5">
                                            {col.label}
                                            {isSortable && isActive && (
                                                <svg className="w-3 h-3 text-primary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d={sortDirection === 'asc' ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                                                </svg>
                                            )}
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                        {sortedGuests.map((g) => {
                            const parentGuest = g._guestType !== 'primary' ? guests.find((pg: any) => pg.id === g._parentId) : g;
                            const openEdit = () => { setSelectedGuest(parentGuest || g); setIsEditGuestModalOpen(true); };
                            const typeBadge = g._guestType === 'primary' ? null
                                : g._guestType === 'plusOne'
                                    ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-purple-50 text-purple-700 border border-purple-100">+1</span>
                                    : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-amber-50 text-amber-700 border border-amber-100">Child</span>;

                            let dietaryGuest = '';
                            let dietaryPlusOne = '';
                            let dietaryChildren: string[] = [];
                            if (g.dietary) {
                                if (typeof g.dietary === 'string') {
                                    dietaryGuest = g.dietary;
                                } else {
                                    dietaryGuest = g.dietary.guest || '';
                                    dietaryPlusOne = g.dietary.plusOne || '';
                                    dietaryChildren = g.dietary.children || [];
                                }
                            }
                            const displayDietary = g._guestType === 'plusOne' 
                                ? dietaryPlusOne 
                                : (g._guestType === 'child' ? (dietaryChildren[g._childIndex] || '') : dietaryGuest);

                            return (
                            <tr 
                                key={g._rowKey} 
                                className={`hover:bg-bg-subtle transition-colors cursor-pointer group ${g._guestType !== 'primary' ? 'bg-gray-50/50' : ''}`}
                                onClick={openEdit}
                            >
                                {visibleColumns.name && <td className="px-6 py-4 whitespace-nowrap font-medium text-secondary">
                                    {g._guestType !== 'primary' && <span className="text-gray-300 mr-2">└</span>}
                                    {g._displayName}
                                </td>}
                                {visibleColumns.type && <td className="px-6 py-4 whitespace-nowrap">
                                    {typeBadge || <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-gray-50 text-secondary-light border border-gray-200">Primary</span>}
                                </td>}
                                {visibleColumns.code && <td className="px-6 py-4 whitespace-nowrap font-mono text-base text-secondary-light">
                                    {g.inviteCode ? (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const url = `${window.location.origin}/rsvp?code=${g.inviteCode}`;
                                                navigator.clipboard.writeText(url);
                                                alert(`Copied link for ${g._displayName}:\n${url}`);
                                            }}
                                            className="flex items-center gap-1 hover:text-primary transition-colors group/link"
                                            title="Click to copy Magic Link"
                                        >
                                            <span className="bg-gray-50 px-2 py-0.5 rounded border border-gray-100">{g.inviteCode}</span>
                                            <svg className="w-4 h-4 opacity-0 group-hover/link:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                            </svg>
                                        </button>
                                    ) : (
                                        <span className="text-gray-300 text-sm italic">Hash only</span>
                                    )}
                                </td>}
                                {visibleColumns.events && <td className="px-6 py-4 whitespace-nowrap">
                                    {g.events?.map((c: string) => (
                                        <span key={c} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider mr-1 ${c === 'IE' ? 'bg-green-50 text-green-700 border border-green-100' : c === 'PB' ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'bg-primary-light/30 text-primary-dark border border-primary-light/50'}`}>
                                            {c}
                                        </span>
                                    ))}
                                </td>}
                                {visibleColumns.rsvp && <td className="px-6 py-4 whitespace-nowrap text-base">
                                    <span className={g.status?.ie === 'yes' ? 'text-green-600 font-bold' : 'text-gray-500'}>{g.status?.ie || '-'}</span>
                                    <span className="mx-2 text-gray-200">/</span>
                                    <span className={g.status?.es === 'yes' ? 'text-green-600 font-bold' : 'text-gray-500'}>{g.status?.es || '-'}</span>
                                    <span className="mx-2 text-gray-200">/</span>
                                    <span className={g.status?.pb === 'yes' ? 'text-green-600 font-bold' : 'text-gray-500'}>{g.status?.pb || '-'}</span>
                                </td>}
                                {visibleColumns.busIe && <td className="px-6 py-4 whitespace-nowrap text-base text-secondary-light">
                                    {g.bus?.ie ? <span className={g.bus.ie === 'yes' ? 'text-primary font-bold uppercase text-xs tracking-wider' : 'uppercase text-xs tracking-wider'}>{g.bus.ie}</span> : '-'}
                                </td>}
                                {visibleColumns.busEsTo && <td className="px-6 py-4 whitespace-nowrap text-base text-secondary-light">
                                    {g.bus?.esTo ? <span className={g.bus.esTo === 'yes' ? 'text-primary font-bold uppercase text-xs tracking-wider' : 'uppercase text-xs tracking-wider'}>{g.bus.esTo}</span> : '-'}
                                </td>}
                                {visibleColumns.busEsReturn && <td className="px-6 py-4 whitespace-nowrap text-base text-secondary-light">
                                    {g.bus?.esReturn ? <span className={g.bus.esReturn !== 'none' ? 'text-primary font-bold uppercase text-xs tracking-wider' : 'uppercase text-xs tracking-wider'}>{g.bus.esReturn}</span> : '-'}
                                </td>}
                                {visibleColumns.plusOne && <td className="px-6 py-4 whitespace-nowrap text-base text-secondary-light">
                                    {g._guestType === 'primary' ? (g.plusOneAllowed ? (g.plusOneName || <span className="text-gray-400 italic">Allowed</span>) : <span className="text-gray-200">-</span>) : <span className="text-gray-200">-</span>}
                                </td>}
                                {visibleColumns.children && <td className="px-6 py-4 whitespace-nowrap text-base text-secondary-light">
                                    {g._guestType === 'primary' ? (g.plusChildren ? (g.children !== undefined ? <span className="font-bold text-primary">{g.children} Kids</span> : <span className="text-gray-400 italic">Allowed</span>) : <span className="text-gray-200">-</span>) : <span className="text-gray-200">-</span>}
                                </td>}
                                {visibleColumns.wp && <td className="px-6 py-4 whitespace-nowrap">
                                    {g._guestType === 'primary' && g.weddingParty && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-tighter bg-accent/10 text-accent border border-accent/20">
                                            WP Member
                                        </span>
                                    )}
                                </td>}
                                {visibleColumns.country && <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-bold bg-gray-50 text-secondary-light border border-gray-200">
                                        {g.country || 'IE'}
                                    </span>
                                </td>}
                                {visibleColumns.table && <td className="px-6 py-4 whitespace-nowrap text-base text-secondary-light">
                                    {g.tableNumber || <span className="text-gray-200">-</span>}
                                </td>}
                                {visibleColumns.hotel && <td className="px-6 py-4 whitespace-nowrap text-base text-secondary-light">
                                    {g._guestType === 'primary' ? (g.accommodation || <span className="text-gray-200">-</span>) : <span className="text-gray-200">-</span>}
                                </td>}
                                {visibleColumns.dietary && <td className="px-6 py-4 whitespace-nowrap text-base text-secondary-light">
                                    {displayDietary || <span className="text-gray-200">-</span>}
                                </td>}
                                {visibleColumns.notes && <td className="px-6 py-4 text-sm text-secondary-light max-w-xs truncate" title={g.notes}>
                                    {g._guestType === 'primary' ? (g.notes || '-') : '-'}
                                </td>}
                                {visibleColumns.actions && (
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center bg-white group-hover:bg-bg-subtle sticky right-0 z-10 border-l border-gray-50 transition-colors">
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); openEdit(); }} 
                                            className="text-secondary-light hover:text-primary transition-colors rounded p-1 focus:ring-2 focus:ring-primary/20"
                                            title="Edit guest"
                                        >
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                        </button>
                                    </td>
                                )}
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {(filter !== 'all' || tableFilter !== 'all' || typeFilter !== 'all') && (
                <div className="flex justify-end mt-4">
                    <div className="bg-primary/5 border border-primary/10 rounded-xl p-4 flex flex-col items-end min-w-[200px]">
                        <span className="text-xs font-bold text-primary uppercase tracking-widest mb-1">Filter Total</span>
                        <span className="text-3xl font-serif text-secondary">
                            {finalGuests.length}
                        </span>
                        <span className="text-[10px] text-secondary-light mt-1 font-medium">People (each row = 1 person)</span>
                    </div>
                </div>
            )}

            <GuestStats guests={guests} />

            <AddGuestModal
                isOpen={isAddGuestModalOpen}
                onClose={() => setIsAddGuestModalOpen(false)}
                onSuccess={() => {
                    fetchGuests();
                }}
            />

            <ImportGuestsModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onSuccess={() => {
                    fetchGuests();
                }}
            />

            <EditGuestModal
                isOpen={isEditGuestModalOpen}
                onClose={() => {
                    setIsEditGuestModalOpen(false);
                    setSelectedGuest(null);
                }}
                onSuccess={() => {
                    fetchGuests();
                }}
                onDelete={handleDeleteGuest}
                guest={selectedGuest}
            />
        </div >
    );
};

// Main Admin Entry Point that handles sub-routes
export const Admin: React.FC = () => {
    return (
        <div className="min-h-screen bg-bg-base text-secondary font-sans">
            <div className="max-w-7xl mx-auto px-4 py-12">
                <Routes>
                    <Route index element={<AdminHome />} />
                    <Route path="guests" element={<Guests />} />
                    <Route path="events" element={<EventsManager />} />
                    <Route path="announcements" element={<AnnouncementsManager />} />
                    <Route path="table-plan" element={<TablePlanViewer />} />
                </Routes>
            </div>
        </div>
    );
};
